import React from 'react';
import { Loader2, AlertCircle } from 'lucide-react';

// Loading component with modern styling
export const Loading = ({ message = 'Loading...' }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '20px'
  }}>
    <div style={{
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      background: 'linear-gradient(135deg, #e85d04 0%, #ff7b29 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'pulse 1.5s ease-in-out infinite'
    }}>
      <Loader2
        style={{
          width: '24px',
          height: '24px',
          color: 'white',
          animation: 'spin 1s linear infinite'
        }}
      />
    </div>
    <p style={{
      color: '#57534e',
      fontSize: '15px',
      fontWeight: '500'
    }}>
      {message}
    </p>
    <style>{`
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(0.95); opacity: 0.8; }
      }
    `}</style>
  </div>
);

// Error message component
export const ErrorMessage = ({ message, onRetry }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '32px'
  }}>
    <div style={{
      width: '64px',
      height: '64px',
      borderRadius: '16px',
      backgroundColor: '#fee2e2',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px'
    }}>
      <AlertCircle style={{ width: '32px', height: '32px', color: '#ef4444' }} />
    </div>
    <h3 style={{
      fontFamily: "'Space Grotesk', sans-serif",
      color: '#1c1917',
      fontSize: '20px',
      fontWeight: '600',
      marginBottom: '8px',
      textAlign: 'center'
    }}>
      Something went wrong
    </h3>
    <p style={{
      color: '#57534e',
      fontSize: '15px',
      textAlign: 'center',
      marginBottom: '24px',
      maxWidth: '400px',
      lineHeight: '1.6'
    }}>
      {message}
    </p>
    {onRetry && (
      <button
        onClick={onRetry}
        style={{
          padding: '12px 24px',
          background: 'linear-gradient(135deg, #e85d04 0%, #ff7b29 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '10px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
          transition: 'all 0.2s ease',
          boxShadow: '0 4px 12px rgba(232, 93, 4, 0.25)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(232, 93, 4, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(232, 93, 4, 0.25)';
        }}
      >
        Try Again
      </button>
    )}
  </div>
);

// Card component with modern styling
export const Card = ({ children, className = '', style = {} }) => (
  <div
    style={{
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      padding: '24px',
      border: '1px solid #f5f5f4',
      transition: 'all 0.3s ease',
      ...style
    }}
    className={className}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -2px rgba(0, 0, 0, 0.05)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.04)';
    }}
  >
    {children}
  </div>
);

// StatCard component with modern styling
export const StatCard = ({ title, value, subtitle, icon: Icon }) => {
  const iconColors = {
    Users: { bg: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)', color: 'white' },
    Calendar: { bg: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)', color: 'white' },
    TrendingUp: { bg: 'linear-gradient(135deg, #22c55e 0%, #4ade80 100%)', color: 'white' },
    Trophy: { bg: 'linear-gradient(135deg, #e85d04 0%, #ff7b29 100%)', color: 'white' },
    Award: { bg: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', color: 'white' }
  };

  const iconName = Icon?.name || 'Users';
  const colors = iconColors[iconName] || iconColors.Users;

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        padding: '20px',
        border: '1px solid #f5f5f4',
        transition: 'all 0.3s ease',
        cursor: 'default'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -2px rgba(0, 0, 0, 0.05)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.04)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            fontSize: '12px',
            color: '#a8a29e',
            marginBottom: '6px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {title}
          </p>
          <p style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: '28px',
            fontWeight: '700',
            color: '#1c1917',
            margin: '0',
            lineHeight: '1.2',
            letterSpacing: '-0.5px'
          }}>
            {value}
          </p>
          {subtitle && (
            <p style={{
              fontSize: '13px',
              color: '#57534e',
              marginTop: '6px',
              fontWeight: '500',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {subtitle}
            </p>
          )}
        </div>
        {Icon && (
          <div style={{
            padding: '12px',
            background: colors.bg,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Icon style={{ width: '22px', height: '22px', color: colors.color }} />
          </div>
        )}
      </div>
    </div>
  );
};
